import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, Client, Insight, InsightFilters, RoadmapItem, RoadmapItemWithCounts, RoadmapModule, RoadmapWorkType, RoadmapHorizon, MarketMood } from '../lib/api';

const MODULE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'navigator', label: 'Navigator' },
  { value: 'studio', label: 'Studio' },
  { value: 'quant_engine', label: 'Quant Engine' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'cross_cutting', label: 'Cross-cutting' },
  { value: 'none', label: 'None' },
];

const URGENCY_OPTIONS = [
  { value: '', label: 'All Urgency' },
  { value: 'blocker', label: 'Blocker' },
  { value: 'strong_want', label: 'Strong Want' },
  { value: 'nice_to_have', label: 'Nice to Have' },
  { value: 'aspirational', label: 'Aspirational' },
  { value: 'unclear', label: 'Unclear' },
];

const FEEDBACK_OPTIONS = [
  { value: '', label: 'All Feedback' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'valid', label: 'Valid' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'needs_revision', label: 'Needs Revision' },
  { value: 'treated', label: 'Treated' },
  { value: 'implemented', label: 'Implemented' },
];

const WORK_TYPE_OPTIONS = [
  { value: '', label: 'All Work Types' },
  { value: 'r_and_d', label: 'R&D' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'product_ux', label: 'Product/UX' },
  { value: 'unclear', label: 'Unclear' },
];

const MARKET_MOOD_OPTIONS = [
  { value: '', label: 'All Moods' },
  { value: 'panic', label: 'Panic' },
  { value: 'bear', label: 'Bear' },
  { value: 'bull', label: 'Bull' },
  { value: 'euphoric', label: 'Euphoric' },
  { value: 'unknown', label: 'Unknown' },
];

const SORT_OPTIONS = [
  { value: 'date_desc', label: 'Date (Newest)' },
  { value: 'date_asc', label: 'Date (Oldest)' },
  { value: 'client', label: 'Client' },
  { value: 'module', label: 'Module' },
  { value: 'urgency', label: 'Urgency' },
  { value: 'feedback', label: 'Feedback Status' },
];

export function Insights() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Stats
  const [stats, setStats] = useState<{
    total: number;
    by_feedback: Array<{ feedback_status: string; count: number }>;
  } | null>(null);

  // Filters
  const [filters, setFilters] = useState<InsightFilters>({
    limit: 50,
    offset: 0,
  });

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Feedback editing
  const [editingFeedback, setEditingFeedback] = useState<string | null>(null);
  const [feedbackNotes, setFeedbackNotes] = useState('');

  // Speaker editing
  const [editingSpeaker, setEditingSpeaker] = useState<string | null>(null);
  const [speakerName, setSpeakerName] = useState('');
  const [speakerType, setSpeakerType] = useState<'cap8' | 'client' | 'unknown'>('unknown');

  // Roadmap linking
  const [insightRoadmapLinks, setInsightRoadmapLinks] = useState<Map<string, RoadmapItem[]>>(new Map());
  const [showLinkModal, setShowLinkModal] = useState<string | null>(null);
  const [roadmapItems, setRoadmapItems] = useState<RoadmapItemWithCounts[]>([]);
  const [linkModalTab, setLinkModalTab] = useState<'existing' | 'new'>('existing');
  const [newRoadmapItem, setNewRoadmapItem] = useState({
    title: '',
    description: '',
    module_touched: 'navigator' as RoadmapModule,
    work_type: 'engineering' as RoadmapWorkType,
    horizon: 'near_term' as RoadmapHorizon,
  });

  useEffect(() => {
    loadClients();
    loadStats();
    loadRoadmapItems();
  }, []);

  useEffect(() => {
    loadInsights();
  }, [filters]);

  const loadRoadmapItems = async () => {
    try {
      // Load all roadmap items (not just active) so users can link to any
      const result = await api.listRoadmapItems({ limit: 100 });
      // Filter out deprioritized items
      setRoadmapItems(result.roadmap_items.filter(item => item.status !== 'deprioritized'));
    } catch {
      // Silent fail
    }
  };

  const loadRoadmapLinksForInsight = async (insightId: string) => {
    try {
      const links = await api.getInsightRoadmapLinks(insightId);
      setInsightRoadmapLinks(prev => new Map(prev).set(insightId, links));
    } catch {
      // Silent fail
    }
  };

  const loadClients = async () => {
    try {
      const clientList = await api.listClients();
      setClients(clientList);
    } catch {
      // Silent fail
    }
  };

  const loadStats = async () => {
    try {
      const s = await api.getInsightStats();
      setStats(s);
    } catch {
      // Silent fail
    }
  };

  const loadInsights = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.listInsights(filters);
      setInsights(result.insights);
      setTotal(result.total);
    } catch (err) {
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key: keyof InsightFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      offset: 0, // Reset pagination
    }));
  };

  const toggleRow = (insightId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(insightId)) {
        next.delete(insightId);
      } else {
        next.add(insightId);
        // Load roadmap links when expanding
        if (!insightRoadmapLinks.has(insightId)) {
          loadRoadmapLinksForInsight(insightId);
        }
      }
      return next;
    });
  };

  const handleLinkToRoadmap = async (insightId: string, roadmapItemId: string) => {
    try {
      await api.linkInsightToRoadmap(roadmapItemId, insightId);
      loadRoadmapLinksForInsight(insightId);
      setShowLinkModal(null);
    } catch {
      setError('Failed to link insight to roadmap item');
    }
  };

  const handleUnlinkFromRoadmap = async (insightId: string, roadmapItemId: string) => {
    try {
      await api.unlinkInsightFromRoadmap(roadmapItemId, insightId);
      loadRoadmapLinksForInsight(insightId);
    } catch {
      setError('Failed to unlink insight from roadmap item');
    }
  };

  const handleCreateAndLink = async (insightId: string) => {
    if (!newRoadmapItem.title.trim()) {
      setError('Title is required');
      return;
    }
    try {
      // Create the new roadmap item
      const created = await api.createRoadmapItem({
        title: newRoadmapItem.title,
        description: newRoadmapItem.description || undefined,
        module_touched: newRoadmapItem.module_touched,
        work_type: newRoadmapItem.work_type,
        horizon: newRoadmapItem.horizon,
        status: 'proposed',
      });
      // Link it to the insight
      await api.linkInsightToRoadmap(created.roadmap_item_id, insightId);
      // Refresh data
      loadRoadmapLinksForInsight(insightId);
      loadRoadmapItems();
      // Reset form and close modal
      setNewRoadmapItem({
        title: '',
        description: '',
        module_touched: 'navigator',
        work_type: 'engineering',
        horizon: 'near_term',
      });
      setLinkModalTab('existing');
      setShowLinkModal(null);
    } catch {
      setError('Failed to create roadmap item');
    }
  };

  const openLinkModal = (insight: Insight) => {
    // Pre-populate the new item form with insight data
    setNewRoadmapItem({
      title: '',
      description: insight.insight_text,
      module_touched: (insight.module_touched === 'none' ? 'cross_cutting' : insight.module_touched) as RoadmapModule,
      work_type: (insight.work_type === 'unclear' ? 'engineering' : insight.work_type) as RoadmapWorkType,
      horizon: 'near_term',
    });
    setLinkModalTab('existing');
    setShowLinkModal(insight.insight_id);
  };

  const handleFeedback = async (insightId: string, status: string) => {
    try {
      const updated = await api.updateFeedback(insightId, status, feedbackNotes || undefined);
      setInsights((prev) =>
        prev.map((i) => (i.insight_id === insightId ? { ...i, ...updated } : i))
      );
      setEditingFeedback(null);
      setFeedbackNotes('');
      loadStats();
    } catch {
      setError('Failed to update feedback');
    }
  };

  const handleSpeakerEdit = (insight: Insight) => {
    setEditingSpeaker(insight.insight_id);
    setSpeakerName(insight.speaker || '');
    setSpeakerType(insight.speaker_type || 'unknown');
  };

  const handleSpeakerSave = async (insightId: string) => {
    try {
      const updated = await api.updateSpeaker(insightId, speakerName, speakerType);
      setInsights((prev) =>
        prev.map((i) => (i.insight_id === insightId ? { ...i, ...updated } : i))
      );
      setEditingSpeaker(null);
      setSpeakerName('');
      setSpeakerType('unknown');
    } catch {
      setError('Failed to update speaker');
    }
  };

  const getUrgencyClass = (urgency: string) => `tag tag-${urgency.replace('_', '-')}`;
  const getFeedbackClass = (status: string) => `tag tag-${status.replace('_', '-')}`;
  const getModuleClass = (module: string) => `tag tag-${module.replace('_', '-')}`;

  const handleNextPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: (prev.offset || 0) + (prev.limit || 50),
    }));
  };

  const handlePrevPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset || 0) - (prev.limit || 50)),
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Insights Browser</h1>
        <p className="page-subtitle">Review and validate extracted insights</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Insights</div>
          </div>
          {stats.by_feedback.map((f) => (
            <div key={f.feedback_status} className="stat-card">
              <div className="stat-value">{f.count}</div>
              <div className="stat-label">{f.feedback_status.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label className="form-label">Client</label>
          <select
            className="form-select"
            value={filters.client_id || ''}
            onChange={(e) => updateFilter('client_id', e.target.value)}
          >
            <option value="">All Clients</option>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Date From</label>
          <input
            type="date"
            className="form-input"
            value={filters.date_start || ''}
            onChange={(e) => updateFilter('date_start', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="form-label">Date To</label>
          <input
            type="date"
            className="form-input"
            value={filters.date_end || ''}
            onChange={(e) => updateFilter('date_end', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="form-label">Module</label>
          <select
            className="form-select"
            value={filters.module_touched || ''}
            onChange={(e) => updateFilter('module_touched', e.target.value)}
          >
            {MODULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Urgency</label>
          <select
            className="form-select"
            value={filters.urgency || ''}
            onChange={(e) => updateFilter('urgency', e.target.value)}
          >
            {URGENCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Work Type</label>
          <select
            className="form-select"
            value={filters.work_type || ''}
            onChange={(e) => updateFilter('work_type', e.target.value)}
          >
            {WORK_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Feedback</label>
          <select
            className="form-select"
            value={filters.feedback_status || ''}
            onChange={(e) => updateFilter('feedback_status', e.target.value)}
          >
            {FEEDBACK_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Market Mood</label>
          <select
            className="form-select"
            value={(filters.market_mood as MarketMood) || ''}
            onChange={(e) => updateFilter('market_mood', e.target.value)}
          >
            {MARKET_MOOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="form-label">Capability</label>
          <input
            type="text"
            className="form-input"
            placeholder="Search tags..."
            value={filters.capability_tag || ''}
            onChange={(e) => updateFilter('capability_tag', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="form-label">Sort By</label>
          <select
            className="form-select"
            value={filters.sort_by || 'date_desc'}
            onChange={(e) => updateFilter('sort_by', e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Insight</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Module</th>
                  <th>Urgency</th>
                  <th>Feedback</th>
                </tr>
              </thead>
              <tbody>
                {insights.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted" style={{ textAlign: 'center' }}>
                      No insights found
                    </td>
                  </tr>
                ) : (
                  insights.map((insight) => (
                    <>
                      <tr
                        key={insight.insight_id}
                        className="insight-row"
                        onClick={() => toggleRow(insight.insight_id)}
                      >
                        <td>
                          <div className="insight-text">{insight.insight_text}</div>
                          <div className="insight-meta">
                            {insight.capability_tag && (
                              <span style={{ marginRight: '0.5rem' }}>#{insight.capability_tag}</span>
                            )}
                            {insight.is_end_client_report === 1 && (
                              <span style={{ color: 'var(--warning)' }}>End-client report</span>
                            )}
                          </div>
                        </td>
                        <td>{insight.client_name}</td>
                        <td>{insight.meeting_date}</td>
                        <td>
                          <span className={getModuleClass(insight.module_touched)}>
                            {insight.module_touched.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <span className={getUrgencyClass(insight.urgency)}>
                            {insight.urgency.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <span className={getFeedbackClass(insight.feedback_status)}>
                            {insight.feedback_status.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                      {expandedRows.has(insight.insight_id) && (
                        <tr key={`${insight.insight_id}-expanded`}>
                          <td colSpan={6} style={{ background: 'var(--bg-tertiary)' }}>
                            <div className="quote">{insight.supporting_quote}</div>

                            {/* Speaker section */}
                            <div className="mt-1 flex items-center gap-1">
                              <span className="text-small text-muted">Speaker:</span>
                              {editingSpeaker === insight.insight_id ? (
                                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    className="form-input"
                                    style={{ width: '150px', padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                                    placeholder="Speaker name"
                                    value={speakerName}
                                    onChange={(e) => setSpeakerName(e.target.value)}
                                  />
                                  <select
                                    className="form-select"
                                    style={{ width: '100px', padding: '0.25rem 0.5rem', fontSize: '0.8125rem' }}
                                    value={speakerType}
                                    onChange={(e) => setSpeakerType(e.target.value as 'cap8' | 'client' | 'unknown')}
                                  >
                                    <option value="unknown">Unknown</option>
                                    <option value="cap8">Cap8</option>
                                    <option value="client">Client</option>
                                  </select>
                                  <button
                                    className="btn btn-primary btn-small"
                                    onClick={() => handleSpeakerSave(insight.insight_id)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-small"
                                    onClick={() => setEditingSpeaker(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <span
                                  className="text-small"
                                  style={{ cursor: 'pointer', borderBottom: '1px dashed var(--text-secondary)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSpeakerEdit(insight);
                                  }}
                                >
                                  {insight.speaker || 'Unknown'}
                                  {insight.speaker_type && insight.speaker_type !== 'unknown' && (
                                    <span className={`tag tag-${insight.speaker_type}`} style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
                                      {insight.speaker_type === 'cap8' ? 'Cap8' : 'Client'}
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>

                            <div className="mt-1 text-small text-muted">
                              Strategy: {insight.strategy_context || 'N/A'} |
                              Confidence: {insight.extraction_confidence}
                            </div>
                            <div className="mt-1 flex gap-1 items-center">
                              <span className="text-small">Feedback:</span>
                              <div className="feedback-controls">
                                <button
                                  className={`feedback-btn ${insight.feedback_status === 'valid' ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeedback(insight.insight_id, 'valid');
                                  }}
                                >
                                  Valid
                                </button>
                                <button
                                  className={`feedback-btn ${insight.feedback_status === 'invalid' ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeedback(insight.insight_id, 'invalid');
                                  }}
                                >
                                  Invalid
                                </button>
                                <button
                                  className={`feedback-btn ${insight.feedback_status === 'needs_revision' ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingFeedback(insight.insight_id);
                                  }}
                                >
                                  Needs Revision
                                </button>
                                <button
                                  className={`feedback-btn ${insight.feedback_status === 'treated' ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeedback(insight.insight_id, 'treated');
                                  }}
                                >
                                  Treated
                                </button>
                                <button
                                  className={`feedback-btn ${insight.feedback_status === 'implemented' ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleFeedback(insight.insight_id, 'implemented');
                                  }}
                                >
                                  Implemented
                                </button>
                              </div>
                            </div>
                            {editingFeedback === insight.insight_id && (
                              <div className="mt-1">
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Add a note about what needs revision..."
                                  value={feedbackNotes}
                                  onChange={(e) => setFeedbackNotes(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div className="mt-1 btn-group">
                                  <button
                                    className="btn btn-primary btn-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFeedback(insight.insight_id, 'needs_revision');
                                    }}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingFeedback(null);
                                      setFeedbackNotes('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            {insight.feedback_notes && (
                              <div className="mt-1 text-small" style={{ color: 'var(--warning)' }}>
                                Note: {insight.feedback_notes}
                              </div>
                            )}

                            {/* Roadmap Links */}
                            <div className="mt-1 flex items-center gap-1">
                              <span className="text-small text-muted">Roadmap:</span>
                              {insightRoadmapLinks.get(insight.insight_id)?.length ? (
                                <div className="flex flex-wrap gap-1">
                                  {insightRoadmapLinks.get(insight.insight_id)?.map((item) => (
                                    <span
                                      key={item.roadmap_item_id}
                                      className="tag tag-capability"
                                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/roadmap/${item.roadmap_item_id}`);
                                      }}
                                    >
                                      {item.title}
                                      <span
                                        style={{ marginLeft: '0.25rem', opacity: 0.7, cursor: 'pointer' }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleUnlinkFromRoadmap(insight.insight_id, item.roadmap_item_id);
                                        }}
                                      >
                                        &times;
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-small text-muted">None</span>
                              )}
                              <button
                                className="btn btn-ghost btn-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openLinkModal(insight);
                                }}
                              >
                                + Link
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-1">
            <span className="text-small text-muted">
              Showing {(filters.offset || 0) + 1}-
              {Math.min((filters.offset || 0) + (filters.limit || 50), total)} of {total}
            </span>
            <div className="btn-group">
              <button
                className="btn btn-secondary btn-small"
                onClick={handlePrevPage}
                disabled={(filters.offset || 0) === 0}
              >
                Prev
              </button>
              <button
                className="btn btn-secondary btn-small"
                onClick={handleNextPage}
                disabled={(filters.offset || 0) + (filters.limit || 50) >= total}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Link to Roadmap Modal */}
      {showLinkModal && (
        <div className="modal-overlay" onClick={() => setShowLinkModal(null)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Link to Roadmap</h2>
              <button className="modal-close" onClick={() => setShowLinkModal(null)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              {/* Tabs */}
              <div className="btn-group mb-1">
                <button
                  className={`btn ${linkModalTab === 'existing' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLinkModalTab('existing')}
                >
                  Link to Existing
                </button>
                <button
                  className={`btn ${linkModalTab === 'new' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setLinkModalTab('new')}
                >
                  Create New Item
                </button>
              </div>

              {linkModalTab === 'existing' ? (
                <>
                  {roadmapItems.length === 0 ? (
                    <div className="text-center" style={{ padding: '2rem' }}>
                      <p className="text-muted mb-1">No roadmap items available</p>
                      <button
                        className="btn btn-primary"
                        onClick={() => setLinkModalTab('new')}
                      >
                        Create New Roadmap Item
                      </button>
                    </div>
                  ) : (
                    <div className="table-container" style={{ maxHeight: '350px' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Module</th>
                            <th>Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {roadmapItems
                            .filter((item) => {
                              const linkedIds = insightRoadmapLinks.get(showLinkModal)?.map(l => l.roadmap_item_id) || [];
                              return !linkedIds.includes(item.roadmap_item_id);
                            })
                            .map((item) => (
                              <tr key={item.roadmap_item_id}>
                                <td>
                                  <div className="insight-text">{item.title}</div>
                                  {item.description && (
                                    <div className="text-small text-muted" style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {item.description}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <span className="tag tag-capability">
                                    {item.module_touched.replace('_', ' ')}
                                  </span>
                                </td>
                                <td>
                                  <span className={`tag ${item.status === 'shipped' ? 'tag-valid' : item.status === 'active' ? 'tag-blocker' : 'tag-warning'}`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td>
                                  <button
                                    className="btn btn-primary btn-small"
                                    onClick={() => handleLinkToRoadmap(showLinkModal, item.roadmap_item_id)}
                                  >
                                    Link
                                  </button>
                                </td>
                              </tr>
                            ))}
                          {roadmapItems.filter((item) => {
                            const linkedIds = insightRoadmapLinks.get(showLinkModal)?.map(l => l.roadmap_item_id) || [];
                            return !linkedIds.includes(item.roadmap_item_id);
                          }).length === 0 && (
                            <tr>
                              <td colSpan={4} className="text-muted text-center">
                                All roadmap items are already linked
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Title *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Enter roadmap item title"
                      value={newRoadmapItem.title}
                      onChange={(e) => setNewRoadmapItem(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      placeholder="Description (pre-filled from insight)"
                      value={newRoadmapItem.description}
                      onChange={(e) => setNewRoadmapItem(prev => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Module</label>
                      <select
                        className="form-select"
                        value={newRoadmapItem.module_touched}
                        onChange={(e) => setNewRoadmapItem(prev => ({ ...prev, module_touched: e.target.value as RoadmapModule }))}
                      >
                        <option value="navigator">Navigator</option>
                        <option value="studio">Studio</option>
                        <option value="quant_engine">Quant Engine</option>
                        <option value="dashboard">Dashboard</option>
                        <option value="cross_cutting">Cross-Cutting</option>
                        <option value="platform">Platform</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Work Type</label>
                      <select
                        className="form-select"
                        value={newRoadmapItem.work_type}
                        onChange={(e) => setNewRoadmapItem(prev => ({ ...prev, work_type: e.target.value as RoadmapWorkType }))}
                      >
                        <option value="r_and_d">R&D</option>
                        <option value="engineering">Engineering</option>
                        <option value="product_ux">Product/UX</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Horizon</label>
                    <select
                      className="form-select"
                      value={newRoadmapItem.horizon}
                      onChange={(e) => setNewRoadmapItem(prev => ({ ...prev, horizon: e.target.value as RoadmapHorizon }))}
                    >
                      <option value="near_term">Near-Term</option>
                      <option value="mid_term">Mid-Term</option>
                      <option value="long_term">Long-Term</option>
                    </select>
                  </div>
                  <div className="flex justify-between mt-1">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setLinkModalTab('existing')}
                    >
                      Back
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleCreateAndLink(showLinkModal)}
                      disabled={!newRoadmapItem.title.trim()}
                    >
                      Create & Link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
